import * as preferencesSelectors from 'companion-app-components/flux/preferences/selectors';

import store from 'store';
import { createSelector } from 'reselect';
import { getThemeByKey } from 'themes/themeBase';

type ThemeKey = 'light' | 'dark';
type ThemePreferenceKey = ThemeKey | 'system';

const DEFAULT_THEME: ThemeKey = 'light';
const DEFAULT_THEME_PREFERENCE: ThemePreferenceKey = 'system';

const normalizeThemePreferenceKey = (themePreferenceKey?: string | null): ThemePreferenceKey => {
  if (themePreferenceKey === 'dark' || themePreferenceKey === 'light' || themePreferenceKey === DEFAULT_THEME_PREFERENCE) {
    return themePreferenceKey;
  }

  return DEFAULT_THEME_PREFERENCE;
};

const getSystemThemeKey = (): ThemeKey => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return DEFAULT_THEME;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : DEFAULT_THEME;
};

const getResolvedThemeKey = (themePreferenceKey?: string | null): ThemeKey => {
  const normalizedThemePreferenceKey = normalizeThemePreferenceKey(themePreferenceKey);

  if (normalizedThemePreferenceKey === 'dark' || normalizedThemePreferenceKey === 'light') {
    return normalizedThemePreferenceKey;
  }

  return getSystemThemeKey();
};

const getStoredThemeKey = (): ThemePreferenceKey | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const themeKey = window.localStorage?.getItem('theme');
  return normalizeThemePreferenceKey(themeKey);
};

const getUserPreferencesThemeKey = (state: any): ThemePreferenceKey => normalizeThemePreferenceKey((
  preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user', path: ['webApp'] }) || {}
).theme);

const getPreferencesLoadingState = (state: any): any => preferencesSelectors.getLoadPendingState(state);

export const getCurrentThemeKey = createSelector(
  getUserPreferencesThemeKey,
  getPreferencesLoadingState,
  (themeKey, preferencesLoading) => {
    if (themeKey !== DEFAULT_THEME_PREFERENCE || preferencesLoading?.shared?.user === false) {
      return getResolvedThemeKey(themeKey);
    }

    if (preferencesLoading?.shared?.user) {
      return getResolvedThemeKey(getStoredThemeKey());
    }

    return getResolvedThemeKey(DEFAULT_THEME_PREFERENCE);
  },
);

export const getCurrentThemePreferenceKey = createSelector(
  getUserPreferencesThemeKey,
  getPreferencesLoadingState,
  (themeKey, preferencesLoading) => {
    if (themeKey !== DEFAULT_THEME_PREFERENCE || preferencesLoading?.shared?.user === false) {
      return themeKey;
    }

    if (preferencesLoading?.shared?.user) {
      return getStoredThemeKey() || DEFAULT_THEME_PREFERENCE;
    }

    return DEFAULT_THEME_PREFERENCE;
  },
);

export const getCurrentTheme = createSelector(
  getCurrentThemeKey,
  (themeKey) => getThemeByKey(themeKey),
);

// don't use this method, unless you know what you are doing
export const dangerouslyGetTheme = () => {
  const state = store.getState();
  const themeKey = state ? getCurrentThemeKey(state) : DEFAULT_THEME;
  return getThemeByKey(themeKey);
};
